/**
 * TweaksContext — makes the app-wide theme / density state (from `useTweaks`)
 * available to any screen without prop-drilling.
 *
 * `useTweaks` used to live inside `TweaksPanel` (a topbar popover). The desktop
 * topbar is gone, so ownership is lifted up to `RootLayout`, which calls
 * `useTweaks(tweaksSync)` once and publishes `{ tweaks, setTweak }` here. The
 * Settings → Appearance page consumes it to drive the theme/density controls;
 * `setTweak` still write-throughs to `PATCH /platform` via the `onPersist`
 * callback wired in `RootLayout`.
 */

import { createContext, useContext } from 'react';
import type { Tweaks } from '@k-os/ui';

export interface TweaksContextValue {
  tweaks: Tweaks;
  setTweak: <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void;
}

const TweaksContext = createContext<TweaksContextValue | null>(null);

export function TweaksProvider({
  value,
  children,
}: {
  value: TweaksContextValue;
  children: React.ReactNode;
}) {
  return <TweaksContext.Provider value={value}>{children}</TweaksContext.Provider>;
}

export function useTweaksContext(): TweaksContextValue {
  const ctx = useContext(TweaksContext);
  if (!ctx) {
    throw new Error('useTweaksContext must be used within a <TweaksProvider>');
  }
  return ctx;
}
